import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Trophy, 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Globe, 
  Users,
  Check,
  Clock
} from "lucide-react";
import { cn } from "@/lib/utils";

interface RatingChange {
  before: number;
  after: number;
  delta: number;
}

interface TeamInfo {
  players: string[];
  avgRating: number;
  avgGlobalRating?: number;
}

interface MatchSummary {
  isGlobalMatch: boolean;
  leagueName?: string;
  score: string;
  winner: "myTeam" | "opponents" | null;
  expectedProbability: number;
  myTeam: TeamInfo;
  opponents: TeamInfo;
  ratingChanges: {
    league?: RatingChange;
    global: RatingChange;
  };
  isUserParticipant?: boolean;
}

interface MatchSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  summary: MatchSummary | null;
  /** True when the current user is a league admin who recorded this match. */
  isAdmin?: boolean;
  /** Called when the admin clicks "Ir al panel de la liga". */
  onGoToLeaguePanel?: () => void;
}

export function MatchSummaryDialog({ open, onClose, summary, isAdmin, onGoToLeaguePanel }: MatchSummaryDialogProps) {
  if (!summary) return null;

  const hasOutcome = summary.winner !== null;
  const won = summary.winner === "myTeam";
  const unexpectedResult = hasOutcome && (won 
    ? summary.expectedProbability < 50 
    : summary.expectedProbability >= 50);

  // Calculate games percentage from score string (e.g., "6-4, 7-5")
  const calculateGamesPercentage = (scoreStr: string): { myGames: number; totalGames: number; percentage: number } => {
    try {
      const sets = scoreStr.split(',').map(s => s.trim());
      let myGames = 0;
      let opponentGames = 0;
      
      for (const set of sets) {
        const [team1, team2] = set.split('-').map(n => parseInt(n.trim(), 10));
        if (!isNaN(team1) && !isNaN(team2)) {
          myGames += team1;
          opponentGames += team2;
        }
      }
      
      const totalGames = myGames + opponentGames;
      const percentage = totalGames > 0 ? Math.round((myGames / totalGames) * 100) : 0;
      
      return { myGames, totalGames, percentage };
    } catch {
      return { myGames: 0, totalGames: 0, percentage: 0 };
    }
  };

  const gamesStats = calculateGamesPercentage(summary.score);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent 
        className="sm:max-w-md max-h-[90vh] flex flex-col" 
        data-testid="dialog-match-summary"
      >
        <DialogHeader className="pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Trophy className={cn("h-5 w-5", won ? "text-green-500" : "text-muted-foreground")} />
            Resumen del Partido
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          {/* Match Type Badge */}
          <div className="flex items-center gap-2">
            {summary.isGlobalMatch ? (
              <Badge variant="outline" className="flex items-center gap-1">
                <Globe className="h-3 w-3" />
                Partido Libre
              </Badge>
            ) : (
              <Badge className="bg-primary/10 text-primary flex items-center gap-1">
                <Trophy className="h-3 w-3" />
                {summary.leagueName}
              </Badge>
            )}
            <Badge variant="outline" className="flex items-center gap-1 text-orange-600 border-orange-300">
              <Clock className="h-3 w-3" />
              Pendiente
            </Badge>
          </div>

          {/* Score and Result */}
          <Card className={cn(
            "border-2",
            !hasOutcome ? "border-border" : won ? "border-green-500/50 bg-green-500/5" : "border-red-500/50 bg-red-500/5"
          )}>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold tracking-tight">
                  {summary.score}
                </div>
                {hasOutcome && (
                  <div className={cn(
                    "text-sm font-medium mt-1",
                    won ? "text-green-600" : "text-red-600"
                  )}>
                    {won ? "Victoria" : "Derrota"}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Teams and Ratings */}
          <div className="grid grid-cols-2 gap-3">
            <Card className={cn(
              "border",
              hasOutcome && won ? "border-green-500/30" : ""
            )}>
              <CardContent className="p-3">
                <div className="flex items-center gap-1 mb-2">
                  <Users className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">
                    {summary.isUserParticipant !== false ? "Tu Equipo" : "Equipo 1"}
                  </span>
                  {hasOutcome && won && <Check className="h-3 w-3 text-green-500 ml-auto" />}
                </div>
                <div className="space-y-1">
                  {summary.myTeam.players.map((player, i) => (
                    <div key={i} className="text-sm font-medium truncate">{player}</div>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Rating: {summary.myTeam.avgRating}
                </div>
              </CardContent>
            </Card>

            <Card className={cn(
              "border",
              hasOutcome && !won ? "border-green-500/30" : ""
            )}>
              <CardContent className="p-3">
                <div className="flex items-center gap-1 mb-2">
                  <Users className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">
                    {summary.isUserParticipant !== false ? "Rivales" : "Equipo 2"}
                  </span>
                  {hasOutcome && !won && <Check className="h-3 w-3 text-green-500 ml-auto" />}
                </div>
                <div className="space-y-1">
                  {summary.opponents.players.map((player, i) => (
                    <div key={i} className="text-sm font-medium truncate">{player}</div>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Rating: {summary.opponents.avgRating}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Expected Probability */}
          <Card className="bg-muted/30">
            <CardContent className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Probabilidad de ganar</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-muted-foreground">{summary.expectedProbability}%</span>
                  {unexpectedResult && (
                    <Badge variant="outline" className="text-xs text-muted-foreground border-muted-foreground/30">
                      {won ? "Sorpresa" : "Favorito"}
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Games Won Percentage */}
              {gamesStats.totalGames > 0 && (
                <div className="flex items-center justify-between border-t pt-3">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Games ganados</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {gamesStats.myGames}/{gamesStats.totalGames}
                    </span>
                    <span className={cn(
                      "text-lg font-bold",
                      gamesStats.percentage >= 50 ? "text-green-600" : "text-red-600"
                    )}>
                      {gamesStats.percentage}%
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rating Changes */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              {!hasOutcome ? (
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              ) : won ? (
                <TrendingUp className="h-4 w-4 text-green-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500" />
              )}
              Impacto en Rankings
            </h4>

            {/* League Rating (if applicable) */}
            {summary.ratingChanges.league && (
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-primary" />
                      <span className="text-sm">Liga</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {summary.ratingChanges.league.before}
                      </span>
                      <span className="text-muted-foreground">→</span>
                      <span className="text-sm font-medium">
                        {summary.ratingChanges.league.after}
                      </span>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-xs font-bold",
                          summary.ratingChanges.league.delta > 0 
                            ? "text-green-600 border-green-300 bg-green-50" 
                            : "text-red-600 border-red-300 bg-red-50"
                        )}
                      >
                        {summary.ratingChanges.league.delta > 0 ? "+" : ""}
                        {summary.ratingChanges.league.delta}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Global Rating */}
            <Card>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">Global</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {summary.ratingChanges.global.before}
                    </span>
                    <span className="text-muted-foreground">→</span>
                    <span className="text-sm font-medium">
                      {summary.ratingChanges.global.after}
                    </span>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-xs font-bold",
                        summary.ratingChanges.global.delta > 0 
                          ? "text-green-600 border-green-300 bg-green-50" 
                          : "text-red-600 border-red-300 bg-red-50"
                      )}
                    >
                      {summary.ratingChanges.global.delta > 0 ? "+" : ""}
                      {summary.ratingChanges.global.delta}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pending / admin notice */}
          {isAdmin ? (
            <div className="rounded-lg p-3 border" style={{ background: "#f0f7ec", borderColor: "#c2d9b0" }}>
              <div className="flex items-start gap-2">
                <svg className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: "#325e22" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <p className="text-xs" style={{ color: "#325e22" }}>
                  Eres admin de esta liga. Puedes <strong>aprobarlo ahora desde el panel de partidos</strong> sin esperar al equipo rival.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
              <p className="text-xs text-orange-700 dark:text-orange-300">
                Los puntos se aplicarán cuando el equipo contrario confirme el resultado. El admin de la liga también puede aprobarlo.
              </p>
            </div>
          )}
        </div>

        {/* CTAs - Fixed at bottom */}
        <div className="shrink-0 pt-4 border-t mt-2 flex flex-col gap-2">
          {isAdmin && onGoToLeaguePanel ? (
            <>
              <Button
                onClick={() => { onGoToLeaguePanel(); onClose(); }}
                className="w-full"
                data-testid="button-go-to-panel"
              >
                Ir al panel de la liga
              </Button>
              <Button
                variant="ghost"
                onClick={onClose}
                className="w-full"
                data-testid="button-close-summary"
              >
                Entendido
              </Button>
            </>
          ) : (
            <Button
              onClick={onClose}
              className="w-full"
              data-testid="button-close-summary"
            >
              Entendido
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
